/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { forwardRef, useImperativeHandle, useRef } from 'react';

import { Button } from '@coursera/cds-core';
import { ThumbsDownFilledIcon, ThumbsDownIcon, ThumbsUpFilledIcon, ThumbsUpIcon } from '@coursera/cds-icons';

import { TooltipWrapper } from 'bundles/common/components/Tooltip';
import type { FeedbackOption } from 'bundles/page-config-common/types/userFeedback';

import _t from 'i18n!nls/product-card';

type Props = {
  productCardVariant?: 'grid' | 'list';
  productType?: string;
  productName?: string;
  currentFeedbackState?: FeedbackOption;
  onThumbsUpClick: (currentFeedbackState: FeedbackOption) => void;
  onThumbsDownClick: (currentFeedbackState: FeedbackOption) => void;
};

export type FeedbackButtonRef = {
  focus: () => void;
};

const styles = {
  // Only need this styling change when the buttons are being used on the grid variant
  // of the product card (used on desktop) because on the list variant, the standard "small"
  // icon size that is being used is a little bigger than this and is appropriate for
  // mobile/tablet screen sizes
  gridVariantProductCard: css`
    svg {
      height: 18px;
    }
  `,
  buttonSpacing: css`
    margin-right: var(--cds-spacing-50);
  `,
};

const FeedbackButtons = forwardRef<FeedbackButtonRef, Props>(
  (
    {
      productCardVariant = 'grid',
      productType = '',
      productName = '',
      currentFeedbackState = '',
      onThumbsUpClick,
      onThumbsDownClick,
    },
    ref
  ) => {
    const dislikeButtonRef = useRef<HTMLButtonElement>(null);
    const likeDescription = _t('Show more content like this');
    const likeAriaLabel = _t('Show more content like this #{productType}, #{productName}', {
      productType,
      productName,
    });
    const dislikeDescription = _t('Show less content like this');
    const dislikeAriaLabel = _t('Show less content like this #{productType}, #{productName}', {
      productType,
      productName,
    });

    const handleThumbsUpClick = (e: React.SyntheticEvent) => {
      e.stopPropagation();
      onThumbsUpClick(currentFeedbackState);
    };

    const handleThumbsDownClick = (e: React.SyntheticEvent) => {
      e.stopPropagation();
      onThumbsDownClick(currentFeedbackState);
    };

    useImperativeHandle(
      ref,
      (): FeedbackButtonRef => ({
        focus() {
          dislikeButtonRef.current?.focus();
        },
      })
    );

    return (
      <div>
        <TooltipWrapper message={likeDescription}>
          <Button
            variant="ghost"
            size="small"
            onClick={handleThumbsUpClick}
            css={[styles.buttonSpacing, productCardVariant === 'grid' && styles.gridVariantProductCard]}
            aria-label={likeAriaLabel}
            aria-pressed={currentFeedbackState === 'LIKE'}
          >
            {currentFeedbackState === 'LIKE' ? (
              <ThumbsUpFilledIcon data-testid="rec-is-liked" />
            ) : (
              <ThumbsUpIcon color="default" />
            )}
          </Button>
        </TooltipWrapper>
        <TooltipWrapper message={dislikeDescription}>
          <Button
            variant="ghost"
            size="small"
            onClick={handleThumbsDownClick}
            css={productCardVariant === 'grid' && styles.gridVariantProductCard}
            aria-label={dislikeAriaLabel}
            aria-pressed={currentFeedbackState === 'DISLIKE'}
            ref={dislikeButtonRef}
          >
            {currentFeedbackState === 'DISLIKE' ? (
              <ThumbsDownFilledIcon data-testid="rec-is-disliked" />
            ) : (
              <ThumbsDownIcon color="default" />
            )}
          </Button>
        </TooltipWrapper>
      </div>
    );
  }
);

export default FeedbackButtons;
